import type {
	PageModel, PhotoModel,
} from '@sosocio/creator-types/project';

/**
 * Freeze the eligible destinations for one input selection. Multiple input files
 * retain first-page-only processing instead of each claiming the remaining pages.
 *
 * @param batchSize Number of input files selected in this interaction.
 * @param targetPageIds Enabled destinations in their original product order.
 * @returns Per-upload destination metadata, or undefined without a destination.
 */
export function createDesignPrintUploadContext(
	batchSize: number,
	targetPageIds: PageModel['id'][],
): PhotoModel['_designPrint'] {
	if (batchSize < 1 || !targetPageIds.length) {
		return undefined;
	}

	return {
		targetPageIds: targetPageIds.slice(
			0,
			batchSize === 1 ? undefined : 1,
		),
	};
}

/**
 * Resolve the ordered suffix of enabled print positions from the original target.
 * Callers supply the product's editable pages, which already exclude disabled
 * print positions; raw page indexes may include gaps and noneditable pages.
 *
 * @param pageModels Enabled editable pages in product order.
 * @param targetPageId Page where the upload interaction began.
 * @returns Destination IDs, including the starting page, or an empty list.
 */
export function getDesignPrintTargetPageIds(
	pageModels: PageModel[],
	targetPageId: PageModel['id'] | undefined,
): PageModel['id'][] {
	const targetIndex = pageModels.findIndex((pageModel) => pageModel.id === targetPageId);

	return targetIndex < 0
		? []
		: pageModels.slice(targetIndex).map((pageModel) => pageModel.id);
}

/**
 * Expand saved uploads into their ordered photo outputs without changing file counts.
 *
 * @param photoModels One primary photo per completed input file.
 * @param getPhotoById Lookup for additional PDF-page photos already stored locally.
 * @returns All saved outputs in file/page order, without duplicate photo IDs.
 */
export function getDesignPrintUploadOutputs(
	photoModels: PhotoModel[],
	getPhotoById: (id: PhotoModel['id']) => PhotoModel | undefined,
): PhotoModel[] {
	const outputs: PhotoModel[] = [];
	const addedIds = new Set<PhotoModel['id']>();

	photoModels.forEach((photoModel) => {
		const ids = photoModel._designPrint?.outputPhotoIds ?? [photoModel.id];

		ids.forEach((id) => {
			const output = id === photoModel.id ? photoModel : getPhotoById(id);

			if (output && !addedIds.has(output.id)) {
				outputs.push(output);
				addedIds.add(output.id);
			}
		});
	});

	return outputs;
}
